import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, Check } from "lucide-react";
import api, { fmtErr } from "../api";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { getGeoAccurate } from "./operator/Dashboard";

const RM_LABEL = { verified: "Verified", confirmed: "Confirmed", out_of_range: "Out of range", no_gps: "No GPS" };

function fileToDataUrl(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

function StopRow({ v, jobBase, onSwipe, onSalt, onPhoto }) {
  const [dragX, setDragX] = useState(0);
  const dragging = useRef(false);
  const startX = useRef(null);
  const dragRef = useRef(0);  // ref mirror: pointerup must see the latest distance even if React batches state
  const fileRef = useRef(null);
  const needsPhoto = v.photo_required && !v.photos;
  const THRESHOLD = 110;

  const onPD = (e) => { if (needsPhoto) return; dragging.current = true; startX.current = e.clientX; };
  const onPM = (e) => {
    if (!dragging.current || startX.current == null) return;
    const dx = Math.min(Math.max(0, e.clientX - startX.current), 240);
    dragRef.current = dx;
    setDragX(dx);
  };
  const onPU = () => {
    if (!dragging.current) return;
    if (dragRef.current >= THRESHOLD) onSwipe(v);
    dragging.current = false;
    startX.current = null;
    dragRef.current = 0;
    setDragX(0);
  };

  return (
    <div className="card-flat space-y-2 p-3" data-testid={`stop-${v.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-bold leading-tight">{v.property_name}</p>
          <p className="text-xs text-muted-foreground">{v.address}{v.owner_name ? ` · ${v.owner_name}` : ""}{v.pass_number > 1 ? ` · pass ${v.pass_number}` : ""}</p>
          {v.access_notes && <p className="mt-1 text-xs text-muted-foreground">{v.access_notes}</p>}
        </div>
        <Link to={`${jobBase}/${v.id}`} data-testid={`stop-open-${v.id}`} className="shrink-0 pt-1 text-xs font-bold text-primary underline">Job sheet</Link>
      </div>
      <div className="flex items-center gap-2">
        <button type="button" data-testid={`salted-toggle-${v.id}`} onClick={() => onSalt(v, !v.salt_applied)}
          className={`flex h-14 flex-1 items-center justify-center gap-2 rounded-md border-2 text-sm font-bold ${v.salt_applied ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>
          {v.salt_applied && <Check size={18} weight="bold" />} Salted{v.salt_applied && v.salt_qty ? ` · ${v.salt_qty}` : ""}
        </button>
        {v.salt_applied && (
          <input type="number" min="0" step="0.5" inputMode="decimal" data-testid={`salt-qty-${v.id}`}
            defaultValue={v.salt_qty ?? ""} placeholder="qty"
            onBlur={(e) => onSalt(v, true, e.target.value === "" ? null : Number(e.target.value))}
            className="field-input h-14 w-20 text-center" />
        )}
        {v.photo_required && (
          <>
            <button type="button" data-testid={`camera-btn-${v.id}`} onClick={() => fileRef.current?.click()}
              className={`flex h-14 w-16 shrink-0 items-center justify-center rounded-md border-2 ${v.photos ? "border-primary text-primary" : "border-destructive text-destructive"}`}>
              {v.photos ? <Check size={24} weight="bold" /> : <Camera size={26} weight="bold" />}
            </button>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid={`camera-input-${v.id}`}
              onChange={async (e) => { const f = e.target.files?.[0]; if (f) onPhoto(v, await fileToDataUrl(f)); e.target.value = ""; }} />
          </>
        )}
      </div>
      <div className="relative select-none overflow-hidden rounded-md" style={{ touchAction: "pan-y" }} data-testid={`swipe-track-${v.id}`}>
        <div className="absolute inset-0 flex items-center rounded-md bg-primary/15 pl-4 text-sm font-bold text-primary">
          {needsPhoto ? "Take the photo first" : "Slide right to finish →"}
        </div>
        <div
          className={`relative flex h-16 items-center justify-center rounded-md text-sm font-bold ${needsPhoto ? "cursor-not-allowed bg-muted text-muted-foreground" : "bg-primary text-primary-foreground"}`}
          style={{ transform: `translateX(${dragX}px)`, transition: dragging.current ? "none" : "transform 0.15s" }}
          onPointerDown={onPD} onPointerMove={onPM} onPointerUp={onPU} onPointerCancel={onPU}
          data-testid={`swipe-done-${v.id}`}>
          {needsPhoto ? "Photo first" : "Swipe → Done"}
        </div>
      </div>
    </div>
  );
}

export default function SnowRoute() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("todo");
  const [undo, setUndo] = useState(null);
  const [jobBase, setJobBase] = useState("/visits");
  const undoTimer = useRef(null);

  const load = useCallback(() => api.get("/snow/route").then((r) => setData(r.data)).catch((e) => toast.error(fmtErr(e))), []);
  useEffect(() => {
    load();
    api.get("/auth/me").then((r) => setJobBase(r.data.role === "subcontractor" ? "/sub/jobs" : "/visits")).catch(() => {});
  }, [load]);

  const swipe = async (v) => {
    try {
      const g = await getGeoAccurate(3000);  // best fix within 3s, never longer — location read ONLY here
      await api.post(`/visits/${v.id}/swipe-done`, g || {});
      navigator.vibrate?.(80);
      toast.success(`${v.property_name} — done`);
      clearTimeout(undoTimer.current);
      setUndo({ visit: v });
      undoTimer.current = setTimeout(() => setUndo(null), 10000);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const doUndo = async () => {
    try {
      await api.post(`/visits/${undo.visit.id}/undo-swipe`);
      toast.success("Undone — stop is back on the route");
      setUndo(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const salt = async (v, on, qty) => {
    try {
      await api.post(`/visits/${v.id}/salt`, { salt_applied: on, ...(qty !== undefined ? { salt_qty: qty } : {}) });
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const photo = async (v, dataUrl) => {
    try {
      const g = await getGeoAccurate(3000);  // GPS fix captured WITH the photo (~3s budget), never longer
      await api.post(`/visits/${v.id}/photos`, { photos: [dataUrl], ...(g || {}) });
      toast.success("Photo saved");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (!data) return <Layout title="Snow route"><p className="text-muted-foreground">Loading…</p></Layout>;
  if (!data.event) return (
    <Layout title="Snow route">
      <div className="card-flat p-6 text-center" data-testid="snow-route-empty">
        <p className="font-bold">No open snow event</p>
        <p className="mt-1 text-sm text-muted-foreground">Start a snow event from the Today board first.</p>
        <Link to="/" data-testid="snow-route-back" className="mt-3 inline-block text-sm font-bold text-primary underline">Back to Today</Link>
      </div>
    </Layout>
  );
  const rows = tab === "todo" ? data.todo : data.done;
  return (
    <Layout title="Snow route">
      <div data-testid="snow-route-view">
        <div className="mb-4 grid grid-cols-2 gap-2">
          <button data-testid="route-tab-todo" onClick={() => setTab("todo")}
            className={`h-14 rounded-md text-sm font-bold ${tab === "todo" ? "bg-primary text-primary-foreground" : "border-2 border-border"}`}>Route ({data.todo.length})</button>
          <button data-testid="route-tab-done" onClick={() => setTab("done")}
            className={`h-14 rounded-md text-sm font-bold ${tab === "done" ? "bg-primary text-primary-foreground" : "border-2 border-border"}`}>Done ({data.done.length})</button>
        </div>
        {tab === "todo" && rows.length === 0 && (
          <p className="card-flat p-6 text-center text-sm font-bold" data-testid="route-all-done">All stops done — nice work.</p>
        )}
        <div className="space-y-3">
          {tab === "todo"
            ? rows.map((v) => <StopRow key={v.id} v={v} jobBase={jobBase} onSwipe={swipe} onSalt={salt} onPhoto={photo} />)
            : rows.map((v) => (
              <div key={v.id} className="card-flat flex items-center justify-between gap-2 p-4" data-testid={`done-${v.id}`}>
                <div>
                  <p className="font-bold leading-tight">{v.property_name}</p>
                  <p className="text-xs text-muted-foreground">{v.address}{v.pass_number > 1 ? ` · pass ${v.pass_number}` : ""}</p>
                  <p className="mt-1 text-xs font-bold" data-testid={`done-match-${v.id}`}>
                    {v.route_match ? RM_LABEL[v.route_match.status] || v.route_match.status : "—"}
                    {v.salt_applied ? ` · Salted${v.salt_qty ? ` ${v.salt_qty}` : ""}` : ""}
                    {v.photo_required ? ` · ${v.photos} photo${v.photos === 1 ? "" : "s"}` : ""}
                    {v.evidence_level ? ` · ${v.evidence_level}` : ""}
                  </p>
                </div>
                <Link to={`${jobBase}/${v.id}`} className="shrink-0 text-xs font-bold text-primary underline">Job sheet</Link>
              </div>
            ))}
        </div>
      </div>
      {undo && (
        <div className="fixed inset-x-0 bottom-0 z-50 p-4" data-testid="undo-bar">
          <div className="card-flat mx-auto flex max-w-lg items-center justify-between gap-3 border-2 border-primary p-3">
            <p className="text-sm font-bold">{undo.visit.property_name} marked done</p>
            <button data-testid="undo-swipe-btn" onClick={doUndo} className="h-14 shrink-0 rounded-md bg-primary px-6 text-sm font-bold text-primary-foreground">Undo</button>
          </div>
        </div>
      )}
    </Layout>
  );
}
